import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, AlertTriangle, ArrowLeft, Lock, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lazy-init Stripe with the publishable key. If the placeholder is still in env,
// loadStripe will reject and we show a friendly setup banner instead of crashing.
let stripePromise = null;
const getStripe = (pk) => {
  if (!pk || pk === "pk_test_REPLACE_ME") return null;
  if (!stripePromise) stripePromise = loadStripe(pk);
  return stripePromise;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderInfo = location.state?.orderInfo;

  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!orderInfo) {
      navigate("/", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cfgRes = await axios.get(`${API}/config/public`);
        if (cancelled) return;
        setConfig(cfgRes.data);
        if (!cfgRes.data.stripe_elements_ready) {
          setError("Stripe Elements isn't configured yet. Paste your real Stripe test keys to enable card / Cash App Pay.");
          setLoading(false);
          return;
        }
        const res = await axios.post(`${API}/stripe/payment-intent`, orderInfo);
        if (cancelled) return;
        setIntent(res.data);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderInfo, navigate]);

  const stripePromiseInstance = useMemo(
    () => intent?.publishable_key ? getStripe(intent.publishable_key) : null,
    [intent?.publishable_key]
  );

  if (loading) {
    return (
      <Shell>
        <div className="text-center py-16">
          <Loader2 size={40} className="mx-auto animate-spin" style={{ color: "var(--awd-ochre)" }} />
          <p className="mt-4 font-display text-xl">Preparing secure checkout…</p>
        </div>
      </Shell>
    );
  }

  if (error || !config?.stripe_elements_ready) {
    return (
      <Shell>
        <div className="max-w-xl mx-auto text-center py-16" data-testid="stripe-not-configured">
          <AlertTriangle size={48} className="mx-auto" style={{ color: "var(--awd-jollof)" }} />
          <h1 className="font-display text-4xl mt-6">Setup required</h1>
          <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>{error}</p>
          <div className="mt-6 p-4 text-left text-xs font-mono" style={{ background: "var(--awd-surface)", border: "1px solid var(--awd-border-2)" }}>
            <div className="label-mini mb-2" style={{ color: "var(--awd-ochre)" }}>Where to paste keys</div>
            <div style={{ color: "var(--awd-text-2)" }}>backend/.env</div>
            <div className="mt-1">STRIPE_SECRET_KEY=sk_test_...</div>
            <div>STRIPE_PUBLISHABLE_KEY=pk_test_...</div>
            <div style={{ color: "var(--awd-text-2)" }} className="mt-3">frontend/.env</div>
            <div>REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_...</div>
            <div className="mt-3" style={{ color: "var(--awd-text-3)" }}>
              Get them: dashboard.stripe.com/test/apikeys
            </div>
          </div>
          <Link to="/" className="btn-ghost mt-8 inline-flex"><ArrowLeft size={16} /> Back to menu</Link>
        </div>
      </Shell>
    );
  }

  if (!intent || !stripePromiseInstance) {
    return (
      <Shell>
        <div className="text-center py-16">
          <AlertTriangle size={48} className="mx-auto" style={{ color: "var(--awd-jollof)" }} />
          <h1 className="font-display text-4xl mt-6">Unable to start checkout</h1>
          <Link to="/" className="btn-primary mt-8 inline-flex">Back to home</Link>
        </div>
      </Shell>
    );
  }

  const options = {
    clientSecret: intent.client_secret,
    appearance: {
      theme: "night",
      variables: {
        colorPrimary: "#EAA636",
        colorBackground: "#1a1614",
        colorText: "#F4F0EC",
        colorDanger: "#9B2226",
        fontFamily: 'Manrope, system-ui, sans-serif',
        fontSizeBase: '15px',
        borderRadius: '0px',
      },
      rules: {
        ".Input": { border: "1px solid #3F3733", padding: "12px" },
        ".Input:focus": { border: "1px solid #EAA636", boxShadow: "none" },
        ".Tab": { border: "1px solid #3F3733" },
        ".Tab--selected": { border: "1px solid #EAA636" },
      },
    },
  };

  return (
    <Shell>
      <div className="max-w-2xl mx-auto" data-testid="elements-checkout">
        <header className="mb-8">
          <div className="eyebrow">Secure Checkout</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Confirm & pay</h1>
          <div className="flex items-center gap-2 mt-3 text-xs uppercase tracking-widest" style={{ color: "var(--awd-text-2)" }}>
            <Lock size={12} /> Encrypted by Stripe · Test mode
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <Elements stripe={stripePromiseInstance} options={options}>
              <PaymentForm orderId={intent.order_id} amount={intent.amount} />
            </Elements>
          </div>

          <aside className="md:col-span-1">
            <div className="p-5 border" style={{ borderColor: "var(--awd-border)", background: "var(--awd-surface)" }}>
              <div className="label-mini mb-3">Order Summary</div>
              {orderInfo.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1">
                  <span>{it.name || it.id} × {it.quantity}</span>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t flex justify-between" style={{ borderColor: "var(--awd-border)" }}>
                <span className="label-mini">Total</span>
                <span className="font-display text-2xl" style={{ color: "var(--awd-ochre)" }}>${intent.amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-4 p-4 text-xs" style={{ border: "1px solid var(--awd-border-2)", color: "var(--awd-text-2)" }}>
              <div className="flex items-center gap-2 mb-2" style={{ color: "var(--awd-ochre)" }}><CreditCard size={14} /> <span className="label-mini">Accepted</span></div>
              Card · Apple Pay · Google Pay · Cash App Pay
            </div>
            <button onClick={() => navigate("/")} className="mt-4 text-xs uppercase tracking-widest link-reveal" style={{ color: "var(--awd-text-3)" }}>← Cancel & return</button>
          </aside>
        </div>
      </div>
    </Shell>
  );
};

const PaymentForm = ({ orderId, amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setErrorMsg("");

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success?order_id=${orderId}`,
      },
    });

    // Stripe redirects on success; reaching this line means there was an immediate validation error.
    if (error) {
      setErrorMsg(error.message || "Payment failed.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />
      {errorMsg && (
        <div className="mt-4 p-3 text-sm" style={{ background: "rgba(155,34,38,0.15)", color: "#ffb4b4", border: "1px solid var(--awd-jollof)" }} data-testid="payment-error">
          {errorMsg}
        </div>
      )}
      <button
        type="submit"
        className="btn-primary w-full mt-6"
        disabled={!stripe || submitting}
        data-testid="pay-now-button"
      >
        {submitting ? <><Loader2 size={16} className="animate-spin" /> Processing…</> : `Pay $${amount.toFixed(2)}`}
      </button>
      <p className="text-[10px] tracking-widest uppercase mt-3 text-center" style={{ color: "var(--awd-text-3)" }}>
        Test mode · No real charges
      </p>
    </form>
  );
};

const Shell = ({ children }) => (
  <div className="min-h-screen" style={{ background: "var(--awd-bg)" }}>
    <div className="kente-stripe" />
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
      {children}
    </div>
  </div>
);

export default CheckoutPage;
